import { moment } from '@hotjar/insights-common/lib/moment';
import { DATE_RANGE_TYPE } from '@hotjar/global-filters';
import type {
  DATE_UNITS,
  AbsoluteDateRange,
  RelativeDateRange,
  DateRange,
} from '@hotjar/global-filters';
import {
  RELATIVE_DATE_RANGE_OPTIONS,
  DEFAULT_RELATIVE_DATE_RANGE_OPTION,
  RELATIVE_DATE_RANGE_LABELS,
} from '../constants-types/HeatmapDateRangeOptions';

interface AbsoluteRange {
  fromTimestamp: number;
  toTimestamp: number;
}

export interface RelativeDateRangeOption {
  label: RelativeDateRangeLabel;
  short: RelativeDateRangeLabelShort;
  amount: number;
  unit: DATE_UNITS;
  type: DATE_RANGE_TYPE.RELATIVE;
}

export const findOptionByShortLabel = (relativeDateLabelShort: string): RelativeDateRangeOption => {
  return (
    RELATIVE_DATE_RANGE_OPTIONS.find((option) => option.short === relativeDateLabelShort) ||
    DEFAULT_RELATIVE_DATE_RANGE_OPTION
  );
};

export const findOptionByAmountAndUnit = (
  amount: number,
  unit: DATE_UNITS,
): RelativeDateRangeOption => {
  return (
    RELATIVE_DATE_RANGE_OPTIONS.find(
      (option) => option.amount === amount && option.unit === unit,
    ) || DEFAULT_RELATIVE_DATE_RANGE_OPTION
  );
};

const calculateAbsoluteDateRange = (
  relativeDateLabelShort: RelativeDateRangeLabelShort,
): AbsoluteRange => {
  const selectedOption = findOptionByShortLabel(relativeDateLabelShort);

  let fromTimestamp = moment().subtract(selectedOption.amount, selectedOption.unit);

  if (selectedOption.label !== RELATIVE_DATE_RANGE_LABELS.LAST_24_HOURS) {
    fromTimestamp = fromTimestamp.startOf('day');
  }

  return {
    fromTimestamp: fromTimestamp.unix(),
    toTimestamp: moment().endOf('day').unix(),
  };
};

export const getAbsoluteDateRange = (
  dateRange: RelativeDateRange | AbsoluteDateRange,
): AbsoluteRange => {
  switch (dateRange.type) {
    case DATE_RANGE_TYPE.ABSOLUTE:
      return {
        fromTimestamp: moment(dateRange.from).startOf('d').unix(),
        toTimestamp: moment(dateRange.to).endOf('day').unix(),
      };
    case DATE_RANGE_TYPE.RELATIVE: {
      const option = findOptionByAmountAndUnit(dateRange.amount, dateRange.unit);
      return calculateAbsoluteDateRange(option.short);
    }
    default:
      throw new Error('Unknown date range type');
  }
};

export const isDurationLongerThan3Months = (dateRange: DateRange): boolean => {
  if (dateRange.type !== DATE_RANGE_TYPE.ABSOLUTE && dateRange.type !== DATE_RANGE_TYPE.RELATIVE)
    return true;

  const range = getAbsoluteDateRange(dateRange);
  const difference = moment
    .unix(range.toTimestamp)
    .diff(moment.unix(range.fromTimestamp), 'months', true);

  // comparing against 3.1 instead of 3 to leave some buffer
  // for edge cases related to start and end of days, especially
  // for custom date ranges.
  return Math.abs(difference) > 3.1;
};
